import { getInstitutionShortLabel } from './institutionAssets';
import { getJupasOrderRankLabel } from './jupasOrderDisplay';
import { getProgrammePrimaryTitle } from './programmeDisplay';

const INSTITUTION_NAME_TO_SURVEY = {
  'The University of Hong Kong': 'HKU',
  'The Chinese University of Hong Kong': 'CUHK',
  'The Hong Kong University of Science and Technology': 'HKUST',
  'City University of Hong Kong': 'CityU',
};

export const INTEREST_LOW_HELP_REASONS = [
  'not_professional',
  'not_relevant',
  'not_specific',
  'too_broad',
  'partially_relevant',
  'other',
];

export function requiresInterestLowHelpReason(helpfulness) {
  const n = Number(helpfulness);
  return Number.isFinite(n) && n >= 1 && n <= 3;
}

export function isInterestLowHelpReasonComplete(commentForm) {
  if (!requiresInterestLowHelpReason(commentForm?.interestHelpfulness)) return true;
  const reason = String(commentForm?.interestLowHelpReason || '').trim();
  if (!INTEREST_LOW_HELP_REASONS.includes(reason)) return false;
  if (reason === 'other') {
    return Boolean(String(commentForm?.interestLowHelpReasonOther || '').trim());
  }
  return true;
}

function scoreMapFromRows(scoreRows) {
  return Object.fromEntries(
    (scoreRows || [])
      .filter((row) => row?.subjectCode && row?.score)
      .map((row) => [row.subjectCode, String(row.score)])
  );
}

function normalizeCsdScore(score) {
  const value = String(score ?? '').trim();
  if (!value || value === 'A' || value.toLowerCase() === 'attained') return 'Attained';
  if (value.toLowerCase() === 'unattained') return 'Unattained';
  return 'Attained';
}

function formatElectives(scoreRows, scoreExtras) {
  const electives = (scoreRows || [])
    .filter((row) => row?.subjectCode && row?.score && !['CHI', 'ENG', 'MAT', 'CSD'].includes(row.subjectCode))
    .map((row) => `${row.subjectCode} ${row.score}`);

  if (scoreExtras?.categoryBCActive && scoreExtras.categoryBCSubject) {
    const categoryBC = [scoreExtras.categoryBCSubject, scoreExtras.categoryBCScore].filter(Boolean).join(' ');
    if (categoryBC) electives.push(categoryBC);
  }

  return electives.join(', ');
}

function mapInstitution(institutionName, language = 'en') {
  const surveyCode = INSTITUTION_NAME_TO_SURVEY[institutionName];
  if (surveyCode) {
    return { institution: surveyCode, institutionOther: '' };
  }
  const label = getInstitutionShortLabel(institutionName, language) || institutionName || 'Other';
  return { institution: 'Other', institutionOther: label.slice(0, 120) };
}

function formatJupasRanking(ranking, language = 'en') {
  return (ranking || [])
    .map((programme, index) => {
      const rankLabel = getJupasOrderRankLabel(index);
      const title = getProgrammePrimaryTitle(programme, language);
      return `${rankLabel} ${programme.code}${title ? ` ${title}` : ''}`.trim();
    })
    .join(' ; ');
}

function formatSpnBonusDetails(scoreExtras) {
  if (!scoreExtras?.spnBonusActive) return '';
  const parts = [scoreExtras.spnBonusScheme, scoreExtras.spnBonusScore].filter(Boolean);
  return parts.join(' · ').slice(0, 500);
}

export function isScoresStepComplete(scoreRows, scoreExtras) {
  const scores = scoreMapFromRows(scoreRows);
  if (!scores.CHI || !scores.ENG || !scores.MAT) return false;
  return Boolean(formatElectives(scoreRows, scoreExtras));
}

export function isRankingStepComplete(ranking) {
  return Array.isArray(ranking) && ranking.length >= 1;
}

export function isCommentStepComplete(commentForm) {
  if (!commentForm?.usedCalculator) return false;
  if (commentForm.usedCalculator === 'yes' && !commentForm.calculatorHelpfulness) return false;
  if (!commentForm?.usedInterestTest) return false;
  if (
    commentForm.usedInterestTest === 'yes'
    && (!commentForm.interestHelpfulness || !commentForm.interestAccuracy)
  ) {
    return false;
  }
  if (commentForm.usedInterestTest === 'yes' && !isInterestLowHelpReasonComplete(commentForm)) {
    return false;
  }
  if (commentForm.volunteerWilling) {
    if (!String(commentForm.volunteerName || '').trim()) return false;
    if (!String(commentForm.volunteerContactMethod || '').trim()) return false;
    if (!String(commentForm.volunteerContact || '').trim()) return false;
  }
  return true;
}

export function getSurveyStepAccess({ scoreRows, scoreExtras, ranking, commentForm }) {
  const scoresComplete = isScoresStepComplete(scoreRows, scoreExtras);
  const rankingComplete = isRankingStepComplete(ranking);
  const commentComplete = isCommentStepComplete(commentForm);

  return {
    score: true,
    ranking: scoresComplete,
    comment: scoresComplete && rankingComplete,
    review: scoresComplete && rankingComplete && commentComplete,
  };
}

export function getHighestAccessibleSurveyStep(stepAccess) {
  if (stepAccess.review) return 'review';
  if (stepAccess.comment) return 'comment';
  if (stepAccess.ranking) return 'ranking';
  return 'score';
}

export function buildJupasSurveyPayload({
  scoreRows,
  scoreExtras,
  ranking,
  commentForm,
  language = 'en',
}) {
  const scores = scoreMapFromRows(scoreRows);
  const admittedProgramme = ranking?.length ? ranking[ranking.length - 1] : null;
  const admittedIndex = ranking?.length ? ranking.length - 1 : -1;
  const { institution, institutionOther } = admittedProgramme
    ? mapInstitution(admittedProgramme.institution, language)
    : { institution: '', institutionOther: '' };

  return {
    jupasCode: admittedProgramme?.code?.toUpperCase() || '',
    institution,
    institutionOther,
    dseChinese: scores.CHI || '',
    dseEnglish: scores.ENG || '',
    dseMath: scores.MAT || '',
    dseCsd: normalizeCsdScore(scores.CSD),
    dseElectives: formatElectives(scoreRows, scoreExtras),
    offerRanking: admittedIndex >= 0 ? getJupasOrderRankLabel(admittedIndex) : '',
    spnBonus: scoreExtras?.spnBonusActive ? 'yes' : 'no',
    spnBonusDetails: formatSpnBonusDetails(scoreExtras),
    jupasRanking: formatJupasRanking(ranking, language),
    usedCalculator: commentForm?.usedCalculator || '',
    calculatorHelpfulness:
      commentForm?.calculatorHelpfulness === '' || commentForm?.calculatorHelpfulness == null
        ? null
        : Number(commentForm.calculatorHelpfulness),
    usedInterestTest: commentForm?.usedInterestTest || '',
    interestHelpfulness:
      commentForm?.interestHelpfulness === '' || commentForm?.interestHelpfulness == null
        ? null
        : Number(commentForm.interestHelpfulness),
    interestLowHelpReason:
      requiresInterestLowHelpReason(commentForm?.interestHelpfulness)
      && INTEREST_LOW_HELP_REASONS.includes(String(commentForm?.interestLowHelpReason || '').trim())
        ? String(commentForm.interestLowHelpReason).trim()
        : null,
    interestLowHelpReasonOther:
      requiresInterestLowHelpReason(commentForm?.interestHelpfulness)
      && String(commentForm?.interestLowHelpReason || '').trim() === 'other'
        ? String(commentForm?.interestLowHelpReasonOther || '').trim().slice(0, 500)
        : '',
    interestAccuracy:
      commentForm?.interestAccuracy === '' || commentForm?.interestAccuracy == null
        ? null
        : Number(commentForm.interestAccuracy),
    improvements: commentForm?.improvements || '',
    freeformFeedback: commentForm?.freeformFeedback || '',
    volunteerWilling: Boolean(commentForm?.volunteerWilling),
    volunteerName: commentForm?.volunteerName || '',
    volunteerProgramme: admittedProgramme
      ? getProgrammePrimaryTitle(admittedProgramme, language).slice(0, 300)
      : '',
    volunteerContact: commentForm?.volunteerContact
      ? [
          commentForm.volunteerContactMethod
            ? String(commentForm.volunteerContactMethod)
            : '',
          String(commentForm.volunteerContact),
        ]
          .filter(Boolean)
          .join(': ')
          .slice(0, 300)
      : '',
  };
}

export function validateJupasSurveySubmission({ scoreRows, scoreExtras, ranking, commentForm }) {
  const scores = scoreMapFromRows(scoreRows);
  const admittedProgramme = ranking?.length ? ranking[ranking.length - 1] : null;

  if (!admittedProgramme?.code) return 'ranking';
  if (!scores.CHI || !scores.ENG || !scores.MAT) return 'coreScores';
  if (!formatElectives(scoreRows, scoreExtras)) return 'electives';

  const { institution, institutionOther } = mapInstitution(admittedProgramme.institution);
  if (!institution) return 'institution';
  if (institution === 'Other' && !institutionOther.trim()) return 'institutionOther';

  if (!commentForm?.usedCalculator) return 'usedCalculator';
  if (commentForm.usedCalculator === 'yes' && !commentForm.calculatorHelpfulness) {
    return 'calculatorHelpfulness';
  }
  if (!commentForm?.usedInterestTest) return 'usedInterestTest';
  if (
    commentForm.usedInterestTest === 'yes'
    && (!commentForm.interestHelpfulness || !commentForm.interestAccuracy)
  ) {
    return 'interestFeedback';
  }
  if (commentForm.usedInterestTest === 'yes' && !isInterestLowHelpReasonComplete(commentForm)) {
    return 'interestLowHelpReason';
  }
  if (
    commentForm.volunteerWilling
    && (
      !String(commentForm.volunteerName || '').trim()
      || !String(commentForm.volunteerContactMethod || '').trim()
      || !String(commentForm.volunteerContact || '').trim()
    )
  ) {
    return 'volunteer';
  }

  return '';
}
